import React, { useEffect, useState } from 'react';
import { Box, Typography, Dialog, IconButton, Divider } from '@mui/material';
import LoginForm from './LoginForm';
import CloseIcon from '@mui/icons-material/Close';
import ContactForm from './ContactForm';
import { useTranslation } from 'react-i18next';
import Profile from './Profile';
import Resume from './Resume';
import { useGetProfileQuery, useVerifyTokenMutation } from '../api/profileApi';
import Topbar from './TopBar';
import Projects from './Projects';
import { useGetExperiencesQuery } from '../api/experienceApi';
import { useGetSkillsByUserIdQuery } from '../api/skillApi';
import { useGetLanguagesByUserIdQuery } from '../api/languageApi';
import { useGetAboutQuery } from '../api/aboutApi';
import HomeSkeleton from './HomeSkeleton';

const Home: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState('en');
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const [verifyToken] = useVerifyTokenMutation();

  const { data: about, isLoading: isLoadingAbout } = useGetAboutQuery();
  const { data: experiences = [], isLoading: isLoadingXp } = useGetExperiencesQuery();
  const { data: profile, isLoading: isLoadingProfile } = useGetProfileQuery();
  const { data: skills = [], isLoading: isLoadingSkill } = useGetSkillsByUserIdQuery(1);
  const { data: languages = [], isLoading: isLoadingLanguage } = useGetLanguagesByUserIdQuery(1)
  const [ready, setReady] = useState(false);
 

  useEffect(() => {
    const checkToken = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const result = await verifyToken({ token }).unwrap();
          setIsLoggedIn(result.isValid);
          if (!result.isValid) localStorage.removeItem('token');
        } catch (err) {
          console.error('Error verifying token:', err);
          localStorage.removeItem('token');
          setIsLoggedIn(false);
        }
      }
    };
    checkToken();
  }, [verifyToken]);

  const isLoadingAll =
  isLoadingAbout ||
  isLoadingXp ||
  isLoadingProfile ||
  isLoadingSkill ||
  isLoadingLanguage;

  useEffect(() => {
    let timer: NodeJS.Timeout;
      timer = setTimeout(() => setReady(true), 300);
    return () => { 
      clearTimeout(timer);
    }
  }, [ready]);

 

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleContactOpen = () => setContactOpen(true);
  const handleContactClose = () => setContactOpen(false);

  const changeLanguage = (lng: string) => {
    setLanguage(lng);
    i18n.changeLanguage(lng);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsLoggedIn(false);
  };

  if (isLoadingAll || !ready ) {
    return (
      <Box sx={{flex: 1, justifyContent: "center", alignItems: "center"}}>
        <Topbar
        isLoggedIn={isLoggedIn}
        onLoginOpen={handleOpen}
        onLogout={handleLogout}
        onContactOpen={handleContactOpen}
        language={language}
        changeLanguage={changeLanguage}
        />
        <HomeSkeleton />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        backgroundColor: '#1a1b1f', // Fond général foncé
        minHeight: '100vh',
        width: '100%',
        overflowX: 'hidden',
        paddingTop: { xs: '62px', md: '60px' }, // espace sous la Topbar
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        pb: 4,
      }}
    >
      <Topbar
        isLoggedIn={isLoggedIn}
        onLoginOpen={handleOpen}
        onLogout={handleLogout}
        onContactOpen={handleContactOpen}
        language={language}
        changeLanguage={changeLanguage}
      />

      {/* Profile Section */}
      <Box
        sx={{
          backgroundColor: '#282c34',
          width: '100%',
          maxWidth: { md: '1200px' },
          borderRadius: '8px',
          // padding: { xs: 2, md: 4 },
          paddingTop: 10,
          boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
          textAlign: 'center',
        }}
      >
        <Profile isLoggedIn={isLoggedIn} profile={profile!} />
        <Resume isLoggedIn={isLoggedIn} skills={skills} languages={languages} experiences={experiences} about={about!} />
        <Projects/>
      </Box>

      {/* Login Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            backgroundColor: '#282c34',
            color: 'white',
            borderRadius: 2,
            padding: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2 }}>
          <Typography variant="h6">{t('login')}</Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon sx={{ color: 'white' }} />
          </IconButton>
        </Box>
        <Divider variant="middle" sx={{ backgroundColor: 'grey.700', mb: 2 }} />
        <LoginForm onClose={handleClose} isLoggedIn={isLoggedIn} setIsLoggedIn={setIsLoggedIn} />
      </Dialog>


      {/* Contact Dialog */}
      <Dialog
        open={contactOpen}
        onClose={handleContactClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            backgroundColor: '#282c34',
            color: 'white',
            borderRadius: 2,
            padding: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2 }}>
          <Typography variant="h6">{t('send_resume')}</Typography>
          <IconButton onClick={handleContactClose}>
            <CloseIcon sx={{ color: 'white' }} />
          </IconButton>
        </Box>
        <Divider variant="middle" sx={{ backgroundColor: 'grey.700', mb: 2 }} />
        <ContactForm handleClose={handleContactClose} />
      </Dialog>
    </Box>
  );
};

export default Home;
